import React, { useEffect, useState } from 'react'
import './index.scss'
import DeerActivityApi from '../../../../../lib/api/deerActivity'
import { BANDS, bandOf, buildDay, dateLabel, RAMP, today } from './model'

const DAYS = 7

const Tracks = ({ size = 12, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox='0 0 12 12' aria-hidden='true'>
    <path fill={color} d='M3.2 1.2c1.4 0 2 2.3 2 4.4 0 1.9-.6 3.4-1.6 3.4S1.7 7.8 1.8 5.9C1.9 3.4 2.3 1.2 3.2 1.2zM8.8 1.2c-1.4 0-2 2.3-2 4.4 0 1.9.6 3.4 1.6 3.4s1.8-1.2 1.8-3.1C10.1 3.4 9.7 1.2 8.8 1.2z' />
  </svg>
)

// Deer movement at a stand: the day's score, its half-hour curve, the best windows and the next seven days.
export default function DeerActivity({ latitude, longitude, token }) {
  const [days, setDays] = useState(null)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let live = true
    setDays(null)
    setFailed(false)
    DeerActivityApi.fetchDays({ lat: latitude, lng: longitude, date: today(), days: DAYS, token })
      .then(res => {
        if (!live) return
        if (Array.isArray(res) && res.length) setDays(res)
        else setFailed(true)
      })
      .catch(err => {
        console.error('Could not fetch deer activity: ', err)
        if (live) setFailed(true)
      })
    return () => { live = false }
  }, [latitude, longitude, token, attempt])

  if (failed) {
    return (
      <section className='deer-activity' aria-label='Deer activity'>
        <div className='deer-activity-title'>Deer Activity</div>
        <div className='deer-activity-error'>
          Deer activity unavailable ·{' '}
          <button className='deer-activity-link' onClick={() => setAttempt(attempt + 1)}>Retry</button>
        </div>
      </section>
    )
  }

  if (!days) {
    return (
      <section className='deer-activity' aria-label='Deer activity' aria-busy='true'>
        <div className='deer-activity-title'>Deer Activity</div>
        <div className='deer-activity-skeleton'>
          <span style={{ width: 64, height: 56 }} />
          <span style={{ flex: 1, height: 14, alignSelf: 'center' }} />
        </div>
        <div className='deer-activity-skeleton'><span style={{ flex: 1, height: 96 }} /></div>
      </section>
    )
  }

  return <DeerActivityView days={days} />
}

// The card itself, for days already loaded: the HuntWind panel's (above) or Artemis's deer_activity
// card (lib/artemis/deer-card), which draws the same thing for a point Artemis was asked about.
// where: what the days strip says the days are for.
export function DeerActivityView({ days, where = 'at this stand', now }) {
  const [selected, setSelected] = useState(0)
  const [whyOpen, setWhyOpen] = useState(false)
  useEffect(() => { setSelected(0) }, [days])

  const day = buildDay(days[Math.min(selected, days.length - 1)], selected, now)
  const { label } = dateLabel(day.date)

  return (
    <section className='deer-activity' aria-label='Deer activity'>
      <div className='deer-activity-head'>
        <div className='deer-activity-head-left'>
          <span className='deer-activity-title'>Deer Activity</span>
          <span className='deer-activity-muted'>{selected === 0 ? 'Today · ' : ''}{label}</span>
        </div>
        <span className={`deer-activity-chip${day.seasonal ? ' seasonal' : ''}`}>
          {day.seasonal ? 'Seasonal pattern' : 'With forecast'}
        </span>
      </div>

      <div className='deer-activity-score'>
        <span className='deer-activity-score-number' style={{ color: day.color }}>{day.score}</span>
        <div className='deer-activity-score-band'>
          <span className='deer-activity-band-word' style={{ color: day.color }}>{day.band.toUpperCase()}</span>
          <div className='deer-activity-meter' aria-hidden='true'>
            {day.meter.map((bg, i) => <span key={i} style={{ background: bg }} />)}
          </div>
          <div className='deer-activity-meter-labels' aria-hidden='true'>
            {BANDS.map(b => <span key={b}>{b.toUpperCase()}</span>)}
          </div>
        </div>
      </div>
      {day.summary ? <p className='deer-activity-summary'>{day.summary}</p> : null}

      <figure className='deer-activity-chart' role='img' aria-label={day.chartLabel}>
        <div className='deer-activity-plot'>
          <div className='deer-activity-night' style={{ left: 0, width: day.sunriseAt }} />
          <div className='deer-activity-night' style={{ left: day.sunsetAt, right: 0 }} />
          {day.windows.map((w, i) => (
            <div key={i} className='deer-activity-zone'
              style={{ left: w.zone.left, width: w.zone.width, borderTop: `2px ${w.style} ${w.color}` }}>
              <span>{w.tag.split(' · ')[0].toUpperCase()}</span>
            </div>
          ))}
          <div className='deer-activity-sunline' style={{ left: day.sunriseAt }} />
          <div className='deer-activity-sunline' style={{ left: day.sunsetAt }} />
          <div className='deer-activity-bars'>
            {day.bars.map((b, i) => (
              <span key={i} title={`${b.time} · ${b.score}`} style={{ height: b.height, background: b.color }} />
            ))}
          </div>
          {day.nowAt ? (
            <div className='deer-activity-now' style={{ left: day.nowAt }}><span>NOW</span></div>
          ) : null}
        </div>
        <div className='deer-activity-axis'>
          <span style={{ left: day.sunriseAt }}>Sunrise {day.sunrise}</span>
          <span style={{ left: day.sunsetAt }}>Sunset {day.sunset}</span>
        </div>
      </figure>

      {day.windows.length ? (
        <div className='deer-activity-windows'>
          {day.windows.map((w, i) => (
            <div key={i} className='deer-activity-window' style={{ border: `1.5px ${w.style} ${w.color}` }}>
              <span className='deer-activity-window-tag' style={{ color: w.color }}>{w.tag.toUpperCase()}</span>
              <span className='deer-activity-window-range'>{w.range}</span>
              <span className='deer-activity-muted'>peaks {w.peakScore} at {w.peak}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className='deer-activity-muted'>No strong window this day</div>
      )}

      <div className='deer-activity-week'>
        <span className='deer-activity-label'>NEXT {days.length} DAYS {where.toUpperCase()}</span>
        <div className='deer-activity-week-grid' style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}>
          {days.map((d, i) => {
            const score = d.context.daylightScore, band = bandOf(score), c = RAMP[band]
            const outline = band === 'Fair' || band === 'Low'
            const { weekday, label: dayLabel } = dateLabel(d.date)
            return (
              <button key={d.date} className={`deer-activity-day${i === selected ? ' selected' : ''}`}
                aria-pressed={i === selected} aria-label={`${dayLabel}, ${score} ${band}`}
                onClick={() => setSelected(i)}>
                <span className='deer-activity-muted'>{i === 0 ? 'Today' : weekday}</span>
                <span className='deer-activity-day-bar' style={{
                  height: Math.round(8 + score / 100 * 26),
                  background: outline ? 'transparent' : c,
                  border: `1.5px ${outline ? 'dashed' : 'solid'} ${c}`
                }} />
                <span className='deer-activity-day-score' style={{ color: c }}>{score}</span>
              </button>
            )
          })}
        </div>
      </div>

      <button className='deer-activity-why-toggle' aria-expanded={whyOpen} onClick={() => setWhyOpen(!whyOpen)}>
        <span>Why this score</span>
        <span className='deer-activity-muted'>{whyOpen ? 'Hide' : 'Show'}</span>
      </button>
      {whyOpen ? (
        <div className='deer-activity-why'>
          {day.why.map(y => (
            <div key={y.key} className='deer-activity-why-row'>
              <span className='deer-activity-label'>{y.key}</span>
              <span>{y.text}</span>
            </div>
          ))}
        </div>
      ) : null}
      <p className='deer-activity-caveat'><Tracks color='#a0a0a0' /> {day.caveat}</p>
    </section>
  )
}
